//Libs
import XMarkIcon from "@heroicons/react/24/solid/XMarkIcon";
import React, { useState, useEffect, useMemo } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import PlacesAutocomplete, {
  geocodeByAddress,
} from "react-places-autocomplete";

//Local
import {
  CustomerLocation,
  ExistingCustomerLocation,
} from "../../models/customer-location";
import BaseModal from "../BaseModal";
import { BillingInfo } from "../../models/customer";
import HeadingOne from "../HeadingOne";
import BaseInputText from "../BaseInputText";
import StyledMessage from "../StyledMessage";
import { ErrorMessage } from "../ErrorMessage";
import BaseButtonSecondary from "../BaseButtonSecondary";
import BaseButtonPrimary from "../BaseButtonPrimary";
import * as strings from "../../strings";
import { logger as devLogger } from "../../logging";

export type TemporaryLocation = Omit<
  CustomerLocation,
  | "customerID"
  | "timestampCreated"
  | "timestampLastModified"
  | "createdBy"
  | "lastModifiedBy"
>;

interface Props {
  /* DATA */
  billingInfo: BillingInfo;
  isDialogOpen: boolean;
  isSubmitting: boolean;
  customerLocationOptions?: ExistingCustomerLocation[] | null;
  /* FUNCTIONS */
  closeDialog: () => void;
  handleUpdateBillingInfo: (billingInfo: BillingInfo) => void;
}

// Form validation schema
const BillingInfoFormSchema = z.object({
  name: z.string().max(1000).optional(),
  email: z.string().max(1000).optional(),
  phone: z.string().max(1000).optional(),
  addressLine1: z.string().min(1, { message: "Address is required" }).max(200),
  addressLine2: z.string().max(200).optional(),
  city: z.string().max(1000).optional(),
  state: z.string().max(1000).optional(),
  zipCode: z.string().max(1000).optional(),
});

export default function EditBillingInfoDialog(props: Props) {
  const [displayError, setDisplayError] = useState<boolean>(false);
  const [displayAddressError, setDisplayAddressError] =
    useState<boolean>(false);

  // Initialize form with billing info from props
  const defaultValues: BillingInfo = useMemo(
    () => ({
      addressLine1: props.billingInfo.addressLine1 ?? "",
      addressLine2: props.billingInfo.addressLine2 ?? "",
      name: props.billingInfo.name ?? "",
      phone: props.billingInfo.phone ?? "",
      email: props.billingInfo.email ?? "",
      city: props.billingInfo.city ?? "",
      zipCode: props.billingInfo.zipCode ?? "",
      state: props.billingInfo.state ?? "",
    }),
    [props.billingInfo],
  );

  // Form setup
  const {
    control,
    formState: { errors },
    reset,
    handleSubmit,
    setValue,
    watch,
  } = useForm<BillingInfo>({
    defaultValues,
    resolver: zodResolver(BillingInfoFormSchema),
    mode: "onChange",
  });

  // Reset form when billingInfo changes
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  // Extract the addressLine1 from the form for the AddressField component
  const addressLine1 = watch("addressLine1");

  const header = (
    <div className="mb-4 flex w-full items-center justify-between rounded-t-lg bg-primary p-8 text-left text-white ">
      <HeadingOne fontColor="text-white" fontSize="text-xl">
        Billing Info
      </HeadingOne>
      <button type="button" onClick={props.closeDialog}>
        <XMarkIcon
          aria-label="close new attribute form"
          className="h-6 text-white"
        />
      </button>
    </div>
  );

  // Get the full address string for a location
  const getLocationAddress = (location: ExistingCustomerLocation): string => {
    let address = location.addressLine1 || "";
    if (location.addressLine2) address += `, ${location.addressLine2}`;
    address += `, ${location.city || ""}, ${location.state || ""} ${location.zipCode || ""}`;
    return address;
  };

  // Handler for when a location is selected from the dropdown
  const handleLocationSelect = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const locationId = event.target.value;
    if (!locationId || !props.customerLocationOptions) return;

    const selectedLocation = props.customerLocationOptions.find(
      (location) => location.id === locationId,
    );
    if (!selectedLocation) return;

    // Update all address fields with the selected location's data
    setValue("addressLine1", selectedLocation.addressLine1 || "");
    setValue("addressLine2", selectedLocation.addressLine2 || "");
    setValue("city", selectedLocation.city || "");
    setValue("state", selectedLocation.state || "");
    setValue("zipCode", selectedLocation.zipCode || "");

    // Keep existing contact info if already entered, otherwise use location data
    // or billing info as fallback for contact fields
    const name = watch("name");
    const email = watch("email");
    const phone = watch("phone");

    if (!name || name === "") setValue("name", props.billingInfo.name || "");

    if (!email || email === "")
      setValue("email", props.billingInfo.email || "");

    if (!phone || phone === "")
      setValue("phone", props.billingInfo.phone || "");
  };

  /**
   * Handles the selection of an address from the Google Places Autocomplete dropdown.
   * This function:
   * 1. Gets the full address details from the Google Geocoding API
   * 2. Extracts individual components (street, city, state, zip)
   * 3. Updates form fields with the appropriate values
   * 4. Clears any address-related error messages
   */
  const handleSelectAddress = async (selectedAddress: string) => {
    if (selectedAddress === "") {
      setDisplayAddressError(true);
    } else {
      try {
        const results = await geocodeByAddress(selectedAddress);
        if (results && results[0]) {
          // Set display address to the formatted address
          setValue("addressLine1", results[0].formatted_address);
          setDisplayAddressError(false);

          // Parse address components from geocoder result
          const components = results[0].address_components;
          let streetNumber = "";
          let street = "";
          let city = "";
          let state = "";
          let zipCode = "";

          components.forEach((component) => {
            const types = component.types;
            if (types.includes("street_number")) {
              streetNumber = component.long_name;
            } else if (types.includes("route")) {
              street = component.long_name;
            } else if (types.includes("locality")) {
              city = component.long_name;
            } else if (types.includes("administrative_area_level_1")) {
              state = component.short_name;
            } else if (types.includes("postal_code")) {
              zipCode = component.long_name;
            }
          });

          // Create address line 1 from street number and street
          const addressLine1 = `${streetNumber} ${street}`.trim();
          setValue(
            "addressLine1",
            addressLine1 !== " " ? addressLine1 : selectedAddress,
          );

          // Set other address fields
          if (city) setValue("city", city);
          if (state) setValue("state", state);
          if (zipCode) setValue("zipCode", zipCode);
        }
      } catch (error) {
        console.error("Error selecting address:", error);
        setDisplayAddressError(true);
      }
    }
  };

  // Form submission handler
  const onSubmit: SubmitHandler<BillingInfo> = async (formValues) => {
    try {
      props.handleUpdateBillingInfo(formValues);
    } catch (error) {
      setDisplayError(true);
      devLogger.error(error);
    }
  };

  // Error message display
  const errorMessage = (
    <ErrorMessage
      message="Something went wrong."
      clearMessage={() => setDisplayError(false)}
    />
  );

  const addressError = (
    <div className="mt-2 text-sm">
      <StyledMessage type="error">
        {{ message: "Address is required" }}
      </StyledMessage>
    </div>
  );

  // Address field with autocomplete
  const addressFieldComponent = (
    <div>
      <PlacesAutocomplete
        value={addressLine1}
        onChange={(newAddress: string) => {
          if (newAddress === "") {
            setDisplayAddressError(true);
          } else {
            setDisplayAddressError(false);
          }
          setValue("addressLine1", newAddress);
        }}
        onSelect={handleSelectAddress}
      >
        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
          <div className="w-full">
            <BaseInputText
              {...getInputProps({
                admin: true,
                required: true,
                inputName: "addressLine1",
                text: "Address",
              })}
            />
            <div
              className={
                suggestions.length > 0 || loading
                  ? "absolute z-10 max-w-sm rounded border bg-white p-2"
                  : ""
              }
            >
              {loading ? (
                <div className="p-2 text-primary">Loading...</div>
              ) : null}

              {suggestions.map((suggestion, suggestionIdx) => {
                const style = {
                  backgroundColor: suggestion.active
                    ? "var(--primary-opacity-90)"
                    : "#fff",
                  borderRadius: "0.25rem",
                  padding: "5px",
                };
                return (
                  <div
                    {...getSuggestionItemProps(suggestion, { style })}
                    key={suggestionIdx}
                  >
                    {suggestion.description}
                  </div>
                );
              })}
            </div>
            {displayAddressError ? addressError : null}
          </div>
        )}
      </PlacesAutocomplete>
    </div>
  );

  return (
    <BaseModal
      closeModal={() => {}}
      open={props.isDialogOpen}
      title={header}
      parentDivStyles="inline-block transform overflow-hidden max-w-screen-lg rounded-lg bg-white text-left align-middle shadow-xl transition-all"
    >
      <form
        autoComplete="off"
        onSubmit={(event) => {
          event.stopPropagation();
          handleSubmit(onSubmit)(event);
        }}
        className="space-y-4 p-4"
      >
        {/* Customer Location Dropdown Section */}
        {props.customerLocationOptions &&
          props.customerLocationOptions.length > 0 && (
            <div className="mb-6 rounded-md border border-green-200 bg-green-50 p-4">
              <div className="mb-2 font-medium text-green-800">
                {props.customerLocationOptions.length === 1
                  ? "Use this location's address as billing address?"
                  : "Select a location to use as billing address"}
              </div>

              <select
                className="w-full rounded-md border border-gray-300 p-2 text-sm"
                onChange={handleLocationSelect}
                defaultValue=""
              >
                <option value="" disabled>
                  -- Select a location address --
                </option>
                {props.customerLocationOptions.map((location) => (
                  <option key={location.id} value={location.id}>
                    {getLocationAddress(location)}
                  </option>
                ))}
              </select>

              <div className="mt-4 text-sm text-green-700">
                Select a location above or enter a new address below:
              </div>
            </div>
          )}

        {/* Two-column layout for form fields */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Left Column - Contact Information */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-700">Contact Information</h3>

            <div>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <BaseInputText
                    text="Name"
                    inputName="name"
                    admin={true}
                    required={false}
                    {...field}
                    value={field.value === null ? "" : field.value}
                  />
                )}
              />
              {errors.name?.message && (
                <div className="mt-2 text-sm">
                  <StyledMessage type="error">
                    {{ message: errors.name.message }}
                  </StyledMessage>
                </div>
              )}
            </div>

            <div>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <BaseInputText
                    text="Phone"
                    inputName="phone"
                    admin={true}
                    required={false}
                    {...field}
                    value={field.value === null ? "" : field.value}
                  />
                )}
              />
              {errors.phone?.message && (
                <div className="mt-2 text-sm">
                  <StyledMessage type="error">
                    {{ message: errors.phone.message }}
                  </StyledMessage>
                </div>
              )}
            </div>

            <div>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <BaseInputText
                    text="Email"
                    inputName="email"
                    admin={true}
                    required={false}
                    {...field}
                    value={field.value === null ? "" : field.value}
                  />
                )}
              />
              {errors.email?.message && (
                <div className="mt-2 text-sm">
                  <StyledMessage type="error">
                    {{ message: errors.email.message }}
                  </StyledMessage>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Address Information */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-700">Address Information</h3>

            {/* AddressField (Google Places Autocomplete) */}
            <div>
              {addressFieldComponent}
              {errors.addressLine1?.message && !displayAddressError && (
                <div className="mt-2 text-sm">
                  <StyledMessage type="error">
                    {{ message: errors.addressLine1.message }}
                  </StyledMessage>
                </div>
              )}
            </div>

            {/* Address Line 2 */}
            <div>
              <Controller
                name="addressLine2"
                control={control}
                render={({ field }) => (
                  <BaseInputText
                    text="Address (cont.)"
                    inputName="addressLine2"
                    admin={true}
                    required={false}
                    {...field}
                    value={field.value === null ? "" : field.value}
                  />
                )}
              />
              {errors.addressLine2?.message && (
                <div className="mt-2 text-sm">
                  <StyledMessage type="error">
                    {{ message: errors.addressLine2.message }}
                  </StyledMessage>
                </div>
              )}
            </div>

            {/* City, State, Zip in a row */}
            <div className="grid grid-cols-3 gap-2">
              {/* City */}
              <div>
                <Controller
                  name="city"
                  control={control}
                  render={({ field }) => (
                    <BaseInputText
                      text="City"
                      inputName="city"
                      admin={true}
                      required={false}
                      {...field}
                      value={field.value === null ? "" : field.value}
                    />
                  )}
                />
                {errors.city?.message && (
                  <div className="mt-2 text-sm">
                    <StyledMessage type="error">
                      {{ message: errors.city.message }}
                    </StyledMessage>
                  </div>
                )}
              </div>

              {/* State */}
              <div>
                <Controller
                  name="state"
                  control={control}
                  render={({ field }) => (
                    <BaseInputText
                      text="State"
                      inputName="state"
                      admin={true}
                      required={false}
                      {...field}
                      value={field.value === null ? "" : field.value}
                    />
                  )}
                />
                {errors.state?.message && (
                  <div className="mt-2 text-sm">
                    <StyledMessage type="error">
                      {{ message: errors.state.message }}
                    </StyledMessage>
                  </div>
                )}
              </div>

              {/* Zip Code */}
              <div>
                <Controller
                  name="zipCode"
                  control={control}
                  render={({ field }) => (
                    <BaseInputText
                      text="Zip Code"
                      inputName="zipCode"
                      admin={true}
                      required={false}
                      {...field}
                      value={field.value === null ? "" : field.value}
                    />
                  )}
                />
                {errors.zipCode?.message && (
                  <div className="mt-2 text-sm">
                    <StyledMessage type="error">
                      {{ message: errors.zipCode.message }}
                    </StyledMessage>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        <div className="mt-4">{displayError ? errorMessage : null}</div>

        {/* Action Buttons */}
        <div className="flex w-full flex-col items-center justify-between gap-4 pt-4 sm:flex-row">
          <BaseButtonSecondary
            type="button"
            className="w-full justify-center uppercase"
            onClick={props.closeDialog}
          >
            {strings.buttons.CANCEL}
          </BaseButtonSecondary>

          <BaseButtonPrimary
            type="submit"
            formNoValidate
            disabled={props.isSubmitting}
            isBusy={props.isSubmitting}
            busyText={strings.buttons.BUSY_SAVING}
            className="w-full justify-center uppercase"
            data-testid="save info"
          >
            {strings.buttons.SAVE}
          </BaseButtonPrimary>
        </div>
      </form>
    </BaseModal>
  );
}
